import { useTranslation } from 'react-i18next'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

const ACTIVE = ['pending', 'running']

/**
 * VH-247 stage 4: one line instead of the eight internal steps. The steps stay in the data and the
 * API (per-step retry, the watchdog); the user sees "being prepared", "ready", or "one part could not
 * be prepared — Try again", which retries whatever failed.
 */
export function CycleProgressLine({ steps = [], onRetry, retrying = false }) {
  const { t } = useTranslation()
  const total = steps.length
  const done = steps.filter((s) => !ACTIVE.includes(s.status)).length
  const failed = steps.filter((s) => s.status === 'failed')
  const active = done < total
  const outOfCredit = failed.some((s) => s.reason === 'ai_out_of_credit')
  const state = active ? 'preparing' : failed.length ? 'failed' : 'ready'

  return (
    <section className="card" aria-label={t('healthCycle.progress.title')} data-testid="cycle-progress" data-state={state}>
      <div className="card-body space-y-2 text-sm">
        {state === 'preparing' && (
          <>
            <p className="flex items-center gap-2 text-gray-900">
              <ArrowPathIcon className="h-5 w-5 text-primary-600 animate-spin" aria-hidden="true" />
              {t('healthCycle.progress.preparing', { done, total })}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-1.5" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
              <div className="h-1.5 rounded-full bg-primary-600" style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
            </div>
          </>
        )}
        {state === 'ready' && (
          <p className="flex items-center gap-2 text-gray-900">
            <CheckCircleIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
            {t('healthCycle.progress.ready')}
          </p>
        )}
        {state === 'failed' && (
          <>
            <p className="flex items-center gap-2 text-gray-900">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
              {t('healthCycle.progress.failed', { count: failed.length })}
            </p>
            {outOfCredit && <p className="text-xs text-red-700">{t('healthCycle.progress.outOfCredit')}</p>}
            {onRetry && (
              <button type="button" className="btn-outline text-xs" disabled={retrying} onClick={onRetry}>
                {t('healthCycle.progress.tryAgain')}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default CycleProgressLine
